import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const ProjectDisplay = ({ 
  title, 
  description, 
  image, 
  type = 'default-project', 
  features = [], 
  techStack = [],
  link,
  featured = false // Add featured prop with default value of false
}) => {
  const [codeLines, setCodeLines] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);

  // Generate random code snippets for display
  useEffect(() => {
    const generateCodeSnippet = () => {
      const codeSnippets = {
        'ecommerce-project': [
          'function addToCart(productId, quantity) {',
          '  const product = getProductById(productId);',
          '  cart.push({ ...product, quantity });',
          '  updateCartUI();',
          '  calculateTotal();',
          '}'
        ],
        'dashboard-project': [
          'async function fetchAnalyticsData() {',
          '  const response = await api.get("/analytics");',
          '  const data = await response.json();',
          '  renderCharts(data);',
          '  updateKPIs(data.metrics);',
          '}'
        ],
        'saas-project': [
          'class SubscriptionManager {',
          '  constructor(userId) {',
          '    this.userId = userId;',
          '    this.plan = null;',
          '  }',
          '  async upgrade(newPlan) {',
          '    // Handle upgrade logic',
          '  }',
          '}'
        ],
        'healthcare-project': [
          'function scheduleAppointment(doctorId, patientId, date) {',
          '  validateAvailability(doctorId, date);',
          '  const appointment = new Appointment(doctorId, patientId);',
          '  appointment.setDateTime(date);',
          '  notifyPatient(patientId);',
          '}'
        ],
        'vr-project': [
          'scene.addEventListener("loaded", function() {',
          '  const camera = document.querySelector("[camera]");',
          '  const models = document.querySelectorAll(".interactive");',
          '  models.forEach(model => {',
          '    model.addEventListener("click", handleInteraction);',
          '  });',
          '}'
        ],
        'financial-project': [
          'function analyzeInvestment(amount, years, rate) {',
          '  let growth = amount;',
          '  for (let i = 0; i < years; i++) {',
          '    growth *= (1 + rate/100);',
          '  }',
          '  return { initial: amount, final: growth };',
          '}'
        ]
      };
      
      return codeSnippets[type] || [
        'function initProject() {',
        '  console.log("Project initialized");',
        '  loadModules();',
        '  renderUI();',
        '}'
      ];
    };
    
    setCodeLines(generateCodeSnippet());
  }, [type]);

  // Generate AI chat simulation
  useEffect(() => {
    const generateChatMessages = () => {
      const chatTemplates = {
        'ecommerce-project': [
          { role: 'bot', message: 'Welcome to our store! How can I help you today?' },
          { role: 'user', message: 'I\'m looking for product recommendations' },
          { role: 'bot', message: 'Based on your preferences, you might like...' }
        ],
        'dashboard-project': [
          { role: 'bot', message: 'Your weekly analytics report is ready' },
          { role: 'user', message: 'Show me conversion trends' },
          { role: 'bot', message: 'Analyzing conversion data...' }
        ],
        'saas-project': [
          { role: 'bot', message: 'Welcome back! Your workspace is 85% complete' },
          { role: 'user', message: 'Schedule team meeting for tomorrow' },
          { role: 'bot', message: 'Meeting scheduled for 10:00 AM' }
        ],
        'healthcare-project': [
          { role: 'bot', message: 'Good morning! Your appointment is confirmed' },
          { role: 'user', message: 'What time is my appointment?' },
          { role: 'bot', message: 'Dr. Smith at 2:30 PM today' }
        ]
      };
      
      return chatTemplates[type] || [
        { role: 'bot', message: 'Hello! How can I assist you with this project?' },
        { role: 'user', message: 'Tell me more about the features' },
        { role: 'bot', message: 'This solution includes several powerful capabilities...' }
      ];
    };
    
    setChatMessages(generateChatMessages());
  }, [type]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimating(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Function to render different project interfaces based on type
  const renderProjectInterface = () => {
    switch (type) {
      case 'ecommerce-project':
        return (
          <div className="ecommerce-interface">
            <div className="browser-header">
              <div className="browser-actions">
                <div className="browser-dot"></div>
                <div className="browser-dot"></div>
                <div className="browser-dot"></div>
              </div>
              <div className="browser-address">
                <div className="address-bar">ecommerce-platform.com</div>
              </div>
            </div>
            <div className="project-interface-grid">
              <div className="product-grid">
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <div key={item} className="product-card" style={{ '--delay': `${item * 0.1}s` }}>
                    <div className="product-image"></div>
                    <div className="product-info">
                      <div className="product-title"></div>
                      <div className="product-price"></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="ai-assistant-container">
                <div className="ai-assistant-header">
                  <div className="ai-avatar"></div>
                  <div className="ai-title">Shopping Assistant</div>
                </div>
                <div className="ai-chat-messages">
                  {chatMessages.map((msg, idx) => (
                    <div 
                      key={idx} 
                      className={`ai-chat-message ${msg.role}`}
                      style={{ animationDelay: `${idx * 0.8}s` }}
                    >
                      <span className="message-content">{msg.message}</span>
                    </div>
                  ))}
                  <div className="ai-typing-indicator">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'dashboard-project':
        return (
          <div className="dashboard-interface">
            <div className="dashboard-header">
              <div className="dashboard-nav">
                <div className="nav-item active"></div>
                <div className="nav-item"></div>
                <div className="nav-item"></div>
              </div>
              <div className="dashboard-search"></div>
            </div>
            <div className="project-interface-grid">
              <div className="dashboard-content">
                <div className="stats-row">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="stat-card">
                      <div className="stat-value"></div>
                      <div className="stat-label"></div>
                    </div>
                  ))}
                </div>
                <div className="chart-grid">
                  <div className="chart line-chart">
                    <div className="chart-line" style={{ bottom: '40%' }}></div>
                    <div className="chart-line" style={{ bottom: '60%' }}></div>
                  </div>
                  <div className="chart bar-chart">
                    {[1, 2, 3, 4].map((item) => (
                      <div 
                        key={item}
                        className="bar"
                        style={{
                          height: `${20 + Math.random() * 60}%`,
                          '--delay': `${item * 0.2}s`
                        }}
                      ></div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="code-snippet-container">
                <div className="code-header">
                  <div className="code-title">analytics.js</div>
                  <div className="code-controls">
                    <span className="code-control"></span>
                    <span className="code-control"></span>
                  </div>
                </div>
                <div className="code-content">
                  {codeLines.map((line, idx) => (
                    <div 
                      key={idx} 
                      className="code-line"
                      style={{ animationDelay: `${idx * 0.1}s` }}
                    >
                      <span className="line-number">{idx + 1}</span>
                      <span className="line-content">{line}</span>
                    </div>
                  ))}
                  <div className="code-cursor"></div>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'saas-project':
        return (
          <div className="saas-interface">
            <div className="app-header">
              <div className="app-logo"></div>
              <div className="app-nav">
                <div className="nav-item active"></div>
                <div className="nav-item"></div>
                <div className="nav-item"></div>
              </div>
              <div className="app-user"></div>
            </div>
            <div className="project-interface-grid">
              <div className="saas-dashboard">
                <div className="saas-widgets">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="saas-widget" style={{ '--delay': `${index * 0.15}s` }}></div>
                  ))}
                </div>
              </div>
              <div className="robot-assistant">
                <div className="robot-head">
                  <div className="robot-eye left"></div>
                  <div className="robot-eye right"></div>
                  <div className="robot-mouth">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="mouth-bar"></div>
                    ))}
                  </div>
                </div>
                <div className="robot-body">
                  <div className="robot-screen">
                    <div className="screen-content">
                      <div className="typing-text">Processing workflow...</div>
                    </div>
                  </div>
                  <div className="robot-buttons">
                    <div className="robot-button"></div>
                    <div className="robot-button"></div>
                    <div className="robot-button"></div>
                  </div>
                </div>
                <div className="robot-status">ACTIVE</div>
              </div>
            </div>
          </div>
        );
        
      default:
        return (
          <div className="tech-interface">
            <div className="interface-header">
              <div className="interface-logo"></div>
              <div className="interface-actions">
                <div className="action-item"></div>
                <div className="action-item"></div>
              </div>
            </div>
            <div className="project-interface-grid">
              <div className="code-display">
                <div className="code-header">
                  <div className="file-tabs">
                    <div className="file-tab active">main.js</div>
                    <div className="file-tab">index.html</div>
                  </div>
                </div>
                <div className="code-content">
                  {codeLines.map((line, idx) => (
                    <div 
                      key={idx} 
                      className="code-line"
                      style={{ animationDelay: `${idx * 0.1}s` }}
                    >
                      <span className="line-number">{idx + 1}</span>
                      <span className="line-content">{line}</span>
                    </div>
                  ))}
                  <div className="code-cursor"></div>
                </div>
              </div>
              <div className="ai-interface">
                <div className="ai-header">AI Assistant</div>
                <div className="ai-visualization">
                  <div className="ai-circles">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div 
                        key={i} 
                        className="ai-circle"
                        style={{
                          '--size': `${10 + Math.random() * 20}px`,
                          '--delay': `${i * 0.2}s`,
                          '--duration': `${2 + Math.random() * 3}s`
                        }}
                      ></div>
                    ))}
                  </div>
                  <div className="ai-connections">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div 
                        key={i} 
                        className="ai-connection"
                        style={{
                          '--rotation': `${i * 30}deg`,
                          '--delay': `${i * 0.1}s`
                        }}
                      ></div>
                    ))}
                  </div>
                </div>
                <div className="ai-chat">
                  {chatMessages.map((msg, idx) => (
                    <div 
                      key={idx} 
                      className={`ai-message ${msg.role}`}
                      style={{ animationDelay: `${idx * 0.8}s` }}
                    >
                      {msg.message}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={`project-display ${type} ${featured ? 'featured' : ''}`}>
      <motion.div 
        className="browser-frame"
        whileHover={{ y: featured ? -8 : -5 }}
        transition={{ duration: 0.3 }}
      >
        {renderProjectInterface()}
        <div className="project-info">
          <h3>{title}</h3>
          <p>{description}</p>
          
          {features && features.length > 0 && (
            <div className="project-features">
              {features.map((feature, index) => (
                <span key={index} className="feature-badge">
                  <span className="feature-icon">✦</span>
                  {feature}
                </span>
              ))}
            </div>
          )}
          
          {techStack && techStack.length > 0 && (
            <div className="tech-stack">
              {techStack.map((tech, index) => (
                <span key={index} className="tech-badge">{tech}</span>
              ))}
            </div>
          )}
          
          <motion.a
            href={link || "#"}
            className="cta-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {featured ? 'Explore Project' : 'View Project'}
            <div className="button-glow"></div>
          </motion.a>
        </div>
      </motion.div>
    </div>
  );
};

export default ProjectDisplay;
